import type { ActionContext } from "vuex";
import type { UserState } from "@/store/user/state";
import type { RootState } from "@/store/types";
import type { User, Account } from "@/models/user";
import type { NavigationFailure } from "vue-router";
import router from "@/router";
import { clearUser } from "./logUserOut";

/**
 * Log the currently logged-in user out of the application and return to the
 * login view.
 * @param context - on which the action is carried out.
 * @param args - The args object.
 * @param args.user - The user object returned from login.
 * @returns NavigationFailure if the navigation to the login view fails or void.
 */
export async function verifyAndSetOnReload(
  context: ActionContext<UserState, RootState>,
  args: {
    user: User;
  }
): Promise<void | NavigationFailure> {
  const user: User = args.user;
  const account: Partial<Account> = user.account as Partial<Account>;

  if (account?.isDeactivated) {
    clearUser(context);
    await router.push(`/login/deactivated/${user.accountId}`);
    return;
  }

  context.commit("updateAccountValues", { ...account });
  context.commit("setAppGroupName", user.lastAppGroupName);
}

/**
 * @param context - on which the action is carried out.
 * @param args - The args object.
 * @param args.user - The user object returned from login.
 * @param args.token - The value of the jwt token.
 * @param args.rememberMe - The value of the remember me in the login form.
 * @returns A boolean, which login uses to decide whether to call further queries or not.
 */
export async function verifyAndSetOnLogin(
  context: ActionContext<UserState, RootState>,
  args: {
    user: User;
    token: string;
    rememberMe: boolean;
  }
): Promise<boolean> {
  const user: User = args.user as User;
  const account: Account = user.account as Account;
  if (account?.isDeactivated) {
    clearUser(context);
    await router.push(`/login/deactivated/${user.accountId}`);
    return false;
  }

  context.commit("setUser", {
    token: args.token,
    user: user,
    rememberMe: args.rememberMe,
  });

  const redirectUrl = sessionStorage.getItem("redirect_url");
  const storedSession = localStorage.getItem("session");
  const sessionObject = storedSession
    ? JSON.parse(window.atob(storedSession))
    : undefined;

  if (redirectUrl) {
    sessionStorage.removeItem("redirect_url");
    // If there is no redirection to specific url and there is a stored url in
    // localStorage
    if (redirectUrl === "/" && sessionObject?.sessionUrl) {
      await router.push(sessionObject?.sessionUrl);
    } else {
      await router.push(redirectUrl);
    }
  } else {
    if (sessionObject?.sessionUrl && sessionObject?.sessionUrl !== "/") {
      await router.push(sessionObject?.sessionUrl);
    } else {
      await router.push({ name: "Checking for Data" });
    }
  }

  return true;
}
